import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { X } from "@phosphor-icons/react";
import "antd/dist/reset.css";
import Loader from "../../Loader/Loader";
import PrintPreview from "../../PrintPreview/PrintPreview";
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ButtonGroup,
  CancelButton,
  ConfirmButton,
  FormSection,
  StyledSelectField,
  PreviewButtonWrapper,
  PreviewButton,
  CancelBtngrp,
} from "./AddLetterheadModalStyled";
import { fetchLetterheadList } from "../../../service/matter";
import { createGlobalStyle } from "styled-components";
import { sendWebSocketMessage } from "../../../middleware/websocketMiddleware";

// Global styles for dropdown
const GlobalDropdownStyle = createGlobalStyle`
  .ant-select-dropdown {
    font-family: "Manrope", sans-serif !important;
    
    .ant-select-item {
      font-family: "Manrope", sans-serif !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      color: #1B2028 !important;
      padding: 8px 12px !important;
      
      &:hover {
        background-color: #f9fafb !important;
      }
    }
    
    .ant-select-item-option-selected {
      background-color: #F1F6FE !important;
      font-weight: 400 !important;
      color: #1B2028 !important;
      
      &:not(.ant-select-item-option-disabled) {
        background-color: #F1F6FE !important;
        font-weight: 400 !important;
      }
    }
    
    .ant-select-item-option-active {
      background-color: #f9fafb !important;
      
      &:not(.ant-select-item-option-disabled) {
        background-color: #f9fafb !important;
      }
    }
  }
`;

const AddLetterheadModal = ({
  isOpen,
  onClose,
  selectedDoc,
  setShowAddLetterheadModal,
  instanceRef,
  isInstanceReady,
  setAutoSave,
}) => {
  const dispatch = useDispatch();
  const [letterheadList, setLetterheadList] = useState([]);
  const [selectedLetterhead, setSelectedLetterhead] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const fetchLetterheads = async () => {
        setIsLoading(true);
        try {
          const response = await fetchLetterheadList();
          // Response structure: { status: "success", data: [...] }
          // axios wraps the response, so response.data contains the API response
          const apiResponse = response?.data;
          const letterheads = apiResponse?.data || [];
          setLetterheadList(letterheads);
          
          // Preselect default letterhead if available
          const defaultLetterhead = letterheads.find((lh) => lh.is_default === true);
          if (defaultLetterhead) {
            setSelectedLetterhead(defaultLetterhead);
          }
        } catch (error) {
          console.error("Error fetching letterhead list:", error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchLetterheads();
    } else {
      // Reset selection when modal closes
      setSelectedLetterhead(null);
    }
  }, [isOpen]);

  const handleLetterheadSelect = (value) => {
    const letterhead = letterheadList.find((lh) => lh.id === value);
    setSelectedLetterhead(letterhead);
  };

  const handlePreview = async () => {
    if (selectedLetterhead) {
      setIsLoading(true);
      try {
        // Fetch the file from the URL and convert to base64
        const response = await fetch(selectedLetterhead.file_url);
        const blob = await response.blob();
        
        // Convert blob to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result.split(',')[1]; // Remove data:type;base64, prefix
          setPreviewData({
            file_type: selectedLetterhead.file_type || "docx",
            file_content: base64String,
            name: selectedLetterhead.name,
          });
          setShowPreview(true);
          setIsLoading(false);
        };
        reader.onerror = () => {
          console.error("Error converting file to base64");
          setIsLoading(false);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error("Error fetching letterhead file:", error);
        setIsLoading(false);
      }
    }
  };

  const handleAdd = async () => {
    if (!selectedLetterhead || !selectedLetterhead.file_url) {
      console.error("No letterhead selected or file_url missing");
      return;
    }
  
    if (!instanceRef?.current || !isInstanceReady) {
      console.error("WebViewer instance not available");
      alert("WebViewer is not ready. Please wait for the document to load.");
      return;
    }
  
    setIsLoading(true);
  
    try {
      const { Core } = instanceRef.current;
      const { documentViewer, annotationManager, Annotations } = Core;
  
      const doc = documentViewer.getDocument();
      if (!doc) {
        throw new Error("No document loaded");
      }
  
      console.log("Applying letterhead as stamp annotation to page 1");

      // Use the PNG URL from backend (file_url is now a PNG thumbnail)
      const letterheadImageUrl = selectedLetterhead.file_url;
      if (!letterheadImageUrl) {
        throw new Error("Letterhead file_url (PNG) is missing");
      }
      console.log("Using letterhead image URL:", letterheadImageUrl);
  
      // ✅ FIX: get page info from Document, not documentViewer
      const pageInfo = doc.getPageInfo(1);
      if (!pageInfo) {
        throw new Error("Could not get page 1 info");
      }
  
      const pageWidth = pageInfo.width;
      const pageHeight = pageInfo.height;
  
      // Create stamp annotation
      const stampAnnotation = new Annotations.StampAnnotation();
      stampAnnotation.PageNumber = 1;
      stampAnnotation.setImageData(letterheadImageUrl);
  
      // Cover full page
      stampAnnotation.X = 0;
      stampAnnotation.Y = 0;
      stampAnnotation.Width = pageWidth;
      stampAnnotation.Height = pageHeight;
  
      // Lock it down
      stampAnnotation.Locked = true;
      stampAnnotation.ReadOnly = true;
      stampAnnotation.NoMove = true;
      stampAnnotation.NoResize = true;
      stampAnnotation.NoDelete = true;
  
      stampAnnotation.Subject = "Letterhead";
      stampAnnotation.Contents = "Letterhead";
      
  
      // Add + render
      annotationManager.addAnnotation(stampAnnotation);
      annotationManager.redrawAnnotation(stampAnnotation);
  
      console.log("Letterhead stamp added successfully");
  
      // -------------------------------
      // Autosave + realtime sync (KEEPING AS-IS)
      // -------------------------------
      if (selectedDoc) {
        try {
          const rawDocId = selectedDoc.id ?? selectedDoc.unique_id?.substring(1);
          const numericDocId = Number(rawDocId);
          const documentId = Number.isFinite(numericDocId) ? numericDocId : String(rawDocId || "");
          const documentName = selectedDoc.name || "Document";
  
          await new Promise((r) => setTimeout(r, 300));
  
          if (setAutoSave) {
            setAutoSave("Autosaving...");
          }
  
          const xfdfCommand =
            await annotationManager.exportAnnotationCommand();
  
          // WebSocket broadcast
          try {
            const userId = Number(localStorage.getItem("user_id"));
  
            dispatch(
              sendWebSocketMessage({
                type: "annotation_change",
                document_id: documentId,
                xfdf_command: xfdfCommand,
                user_id: userId,
              })
            );
  
            console.log(
              "[WS] Letterhead change sent as XFDF annotation command"
            );
          } catch (wsErr) {
            console.error("[WS] Failed to send letterhead XFDF:", wsErr);
          }
  

                setAutoSave(`Saved to ${documentName}`);
                setTimeout(() => setAutoSave(null), 5000);

        } catch (err) {
          console.error("Autosave flow failed:", err);
          if (setAutoSave) setAutoSave(null);
        }
      }
  
      if (onClose) onClose();
      if (setShowAddLetterheadModal) setShowAddLetterheadModal(false);
    } catch (error) {
      console.error("Error adding letterhead:", error);
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
    if (setShowAddLetterheadModal) {
      setShowAddLetterheadModal(false);
    }
  };

  if (showPreview && previewData) {
    return (
      <PrintPreview
        printData={previewData}
        setOpenPreview={handleClosePreview}
        type="letterhead"
      />
    );
  }

  if (!isOpen) return null;

  // Format dropdown options with default label at right end
  const formatLetterheadLabel = (letterhead) => {
    const firmName = letterhead.firm_name || "Firm";
    const location = letterhead.firm_location || "";
    const locationPart = location ? `/${location}` : "";
    return `${firmName}${locationPart}`;
  };

  return (
    <>
      <GlobalDropdownStyle />
      <Overlay>
        <ModalContainer>
        {isLoading && <Loader />}
        <ModalHeader>
          <ModalTitle>Add Letterhead</ModalTitle>
          <X style={{ cursor: "pointer" }} size={20} onClick={handleClose} />
        </ModalHeader>
        <ModalContent>
          <FormSection>
            <StyledSelectField
              placeholder="Choose a letterhead for the document"
              value={selectedLetterhead?.id}
              onChange={handleLetterheadSelect}
              showSearch
              filterOption={(input, option) => {
                // Extract text from the option label (which is now JSX)
                const letterhead = letterheadList.find(lh => lh.id === option.value);
                if (!letterhead) return false;
                const searchText = formatLetterheadLabel(letterhead).toLowerCase();
                return searchText.includes(input.toLowerCase());
              }}
              options={letterheadList.map((letterhead) => ({
                label: (
                  <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span>{formatLetterheadLabel(letterhead)}</span>
                    {letterhead.is_default && (
                      <span style={{ color: '#5A6C87', marginLeft: 'auto' }}>(default)</span>
                    )}
                  </span>
                ),
                value: letterhead.id,
              }))}
              getPopupContainer={(trigger) => trigger.parentElement}
              dropdownStyle={{
                fontFamily: "Manrope, sans-serif",
              }}
            />
            
          </FormSection>
        </ModalContent>
        <ButtonGroup>
          <PreviewButtonWrapper>
              <PreviewButton
                onClick={handlePreview}
                disabled={!selectedLetterhead}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginRight: "8px" }}
                >
                  <path
                    d="M8 2C4.5 2 1.73 4.11 0 7C1.73 9.89 4.5 12 8 12C11.5 12 14.27 9.89 16 7C14.27 4.11 11.5 2 8 2ZM8 10.5C6.07 10.5 4.5 8.93 4.5 7C4.5 5.07 6.07 3.5 8 3.5C9.93 3.5 11.5 5.07 11.5 7C11.5 8.93 9.93 10.5 8 10.5ZM8 5C6.9 5 6 5.9 6 7C6 8.1 6.9 9 8 9C9.1 9 10 8.1 10 7C10 5.9 9.1 5 8 5Z"
                    fill={selectedLetterhead ? "#0C53BB" : "#AEB8C4"}
                  />
                </svg>
                Preview
              </PreviewButton>
            </PreviewButtonWrapper>
            <CancelBtngrp>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <ConfirmButton onClick={handleAdd} disabled={!selectedLetterhead || isLoading}>
            Add
          </ConfirmButton>
          </CancelBtngrp>
        </ButtonGroup>
      </ModalContainer>
    </Overlay>
    </>
  );
};

AddLetterheadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  selectedDoc: PropTypes.object,
  setShowAddLetterheadModal: PropTypes.func,
  instanceRef: PropTypes.object,
  isInstanceReady: PropTypes.bool,
  setAutoSave: PropTypes.func,
};

export default AddLetterheadModal;
